import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Heart,
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

const COPY = {
  de: {
    eyebrow: "Hilfe & Krisenkontakte",
    title: "Du bist nicht allein.",
    intro:
      "RED FLAG zeigt die Warnsignale. Hier findest du die Stellen, die handeln. Anonym, kostenlos, oft rund um die Uhr.",
    emergencyTitle: "Akute Lebensgefahr",
    emergencyBody:
      "Wenn du oder eine andere Person in unmittelbarer Gefahr seid: ruf sofort den Notruf. Nicht zögern.",
    sosCountries: [
      { country: "Schweiz", num: "144", who: "Sanität · Notruf" },
      { country: "Deutschland", num: "112", who: "Notruf EU" },
      { country: "Österreich", num: "144", who: "Rettung" },
    ],
    helplineTitle: "Wenn du reden brauchst",
    helplines: [
      {
        country: "Schweiz",
        name: "Die Dargebotene Hand",
        num: "143",
        hours: "24 / 7 · kostenlos",
        url: "https://www.143.ch",
      },
      {
        country: "Deutschland",
        name: "Telefonseelsorge",
        num: "0800 111 0 111",
        hours: "24 / 7 · kostenlos · anonym",
        url: "https://www.telefonseelsorge.de",
      },
      {
        country: "Deutschland",
        name: "Telefonseelsorge (2)",
        num: "0800 111 0 222",
        hours: "alternative Nummer",
        url: "https://www.telefonseelsorge.de",
      },
      {
        country: "Österreich",
        name: "Telefonseelsorge AT",
        num: "142",
        hours: "24 / 7 · kostenlos",
        url: "https://www.telefonseelsorge.at",
      },
    ],
    domesticTitle: "Häusliche Gewalt & toxische Beziehungen",
    domestic: [
      {
        country: "Schweiz",
        name: "Opferhilfe Schweiz",
        num: "0848 28 28 28",
        hours: "24 / 7",
        url: "https://www.opferhilfe-schweiz.ch",
      },
      {
        country: "Deutschland",
        name: "Hilfetelefon Gewalt gegen Frauen",
        num: "08000 116 016",
        hours: "24 / 7 · kostenlos",
        url: "https://www.hilfetelefon.de",
      },
      {
        country: "Deutschland",
        name: "Hilfetelefon Gewalt an Männern",
        num: "0800 123 99 00",
        hours: "Mo-Do 8-20, Fr 8-15",
        url: "https://www.maennerhilfetelefon.de",
      },
      {
        country: "Österreich",
        name: "Frauenhelpline gegen Gewalt",
        num: "0800 222 555",
        hours: "24 / 7 · kostenlos",
        url: "https://www.frauenhelpline.at",
      },
    ],
    psyTitle: "Psychische Gesundheit",
    psy: [
      {
        country: "Schweiz",
        name: "Pro Mente Sana",
        num: "0848 800 858",
        hours: "Mo, Di, Do 9-12 / 14-17",
        url: "https://www.promentesana.ch",
      },
      {
        country: "Deutschland",
        name: "Info-Telefon Depression",
        num: "0800 33 44 533",
        hours: "Mo-Do 13-17, Fr 13-16",
        url: "https://www.deutsche-depressionshilfe.de",
      },
      {
        country: "Schweiz/DE/AT",
        name: "Nummer gegen Kummer (Kinder/Jugendliche)",
        num: "147 (CH) · 116 111 (DE/AT)",
        hours: "anonym, kostenlos",
        url: "https://www.147.ch",
      },
    ],
    partnersTitle: "Unsere Wege weiter",
    partners: [
      {
        name: "Schweizer Akademie für Transformation GmbH",
        role: "Beratung & Transformation",
        body:
          "Wenn du erkannt hast und jetzt verändern willst — die SAT bietet 1:1-Beratung, Coaching und langfristige Begleitung.",
      },
      {
        name: "Herzbewegung",
        role: "Community & Hilfe",
        body:
          "Eine Gemeinschaft von Menschen, die hinsehen statt wegzuschauen. Austausch, soziale Projekte, Vernetzung.",
      },
    ],
    note: "Wichtig",
    noteBody:
      "RED FLAG ersetzt keine Therapie. Wenn du dauerhaft leidest, hol dir bitte professionelle Unterstützung — die Stellen oben helfen dir dabei.",
    back: "Zurück zur Startseite",
  },
  en: {
    eyebrow: "Help & crisis contacts",
    title: "You're not alone.",
    intro:
      "RED FLAG names the warning signs. Below are the people who act. Anonymous, free, often round the clock.",
    emergencyTitle: "Immediate danger",
    emergencyBody:
      "If you or someone else is in immediate danger: call emergency services now. Do not wait.",
    sosCountries: [
      { country: "Switzerland", num: "144", who: "Ambulance · Emergency" },
      { country: "Germany", num: "112", who: "EU emergency" },
      { country: "Austria", num: "144", who: "Ambulance" },
    ],
    helplineTitle: "When you need to talk",
    helplines: [
      { country: "Switzerland", name: "Die Dargebotene Hand", num: "143", hours: "24/7 · free", url: "https://www.143.ch" },
      { country: "Germany", name: "Telefonseelsorge", num: "0800 111 0 111", hours: "24/7 · free · anonymous", url: "https://www.telefonseelsorge.de" },
      { country: "Germany", name: "Telefonseelsorge (2)", num: "0800 111 0 222", hours: "alternative line", url: "https://www.telefonseelsorge.de" },
      { country: "Austria", name: "Telefonseelsorge AT", num: "142", hours: "24/7 · free", url: "https://www.telefonseelsorge.at" },
    ],
    domesticTitle: "Domestic violence & toxic relationships",
    domestic: [
      { country: "Switzerland", name: "Opferhilfe Schweiz", num: "0848 28 28 28", hours: "24/7", url: "https://www.opferhilfe-schweiz.ch" },
      { country: "Germany", name: "Hilfetelefon Gewalt gegen Frauen", num: "08000 116 016", hours: "24/7 · free", url: "https://www.hilfetelefon.de" },
      { country: "Germany", name: "Hilfetelefon Männer", num: "0800 123 99 00", hours: "Mon-Thu 8-20, Fri 8-15", url: "https://www.maennerhilfetelefon.de" },
      { country: "Austria", name: "Frauenhelpline gegen Gewalt", num: "0800 222 555", hours: "24/7 · free", url: "https://www.frauenhelpline.at" },
    ],
    psyTitle: "Mental health",
    psy: [
      { country: "Switzerland", name: "Pro Mente Sana", num: "0848 800 858", hours: "Mo, Tue, Thu 9-12 / 14-17", url: "https://www.promentesana.ch" },
      { country: "Germany", name: "Info-Telefon Depression", num: "0800 33 44 533", hours: "Mon-Thu 13-17, Fri 13-16", url: "https://www.deutsche-depressionshilfe.de" },
      { country: "CH/DE/AT", name: "Nummer gegen Kummer (kids/teens)", num: "147 (CH) · 116 111 (DE/AT)", hours: "anonymous, free", url: "https://www.147.ch" },
    ],
    partnersTitle: "Our paths forward",
    partners: [
      { name: "Swiss Academy for Transformation GmbH", role: "Coaching & transformation", body: "Once you've recognised the patterns and want to transform — SAT offers 1:1 counselling, coaching and long-term support." },
      { name: "Herzbewegung", role: "Community & support", body: "A community of people who choose to look instead of looking away. Exchange, social projects, connection." },
    ],
    note: "Important",
    noteBody:
      "RED FLAG does not replace therapy. If you are suffering long-term, please get professional support — the resources above help you with that.",
    back: "Back to home",
  },
};

function ContactRow({ row }) {
  return (
    <div
      data-testid={`help-row-${row.num.replace(/\s+/g, "")}`}
      className="bg-[#0A0A0A] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
    >
      <div className="flex-1 min-w-0">
        <div className="font-mono-plex text-[10px] uppercase tracking-widest text-redflag">
          {row.country}
        </div>
        <div className="mt-1 font-medium text-white text-base">{row.name}</div>
        <div className="text-xs text-white/55">{row.hours}</div>
      </div>
      <div className="flex items-center gap-3">
        <a
          href={`tel:${row.num.replace(/\s+/g, "")}`}
          className="font-display uppercase text-xl text-redflag tracking-tight"
        >
          {row.num}
        </a>
        {row.url && (
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/55 hover:text-redflag transition-colors"
            aria-label="Open website"
          >
            <ExternalLink size={16} />
          </a>
        )}
      </div>
    </div>
  );
}

function Block({ title, icon, children }) {
  return (
    <section className="mt-14">
      <div className="flex items-center gap-3 mb-5">
        {icon}
        <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight">
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {children}
      </div>
    </section>
  );
}

export default function Help() {
  const { lang } = useLanguage();
  const t = COPY[lang];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main data-testid="help-page" className="pt-28 md:pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link
            to="/"
            data-testid="help-back"
            className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-redflag transition-colors font-mono-plex uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> {t.back}
          </Link>

          <p className="overline mt-8">{t.eyebrow}</p>
          <h1 className="font-display uppercase mt-4 text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            {t.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed max-w-3xl">
            {t.intro}
          </p>

          {/* Acute emergency */}
          <div className="mt-10 border-2 border-redflag bg-[#160708] p-6 md:p-8">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} className="text-redflag live-dot" />
              <div className="font-display uppercase text-xl text-white">
                {t.emergencyTitle}
              </div>
            </div>
            <p className="mt-2 text-sm text-white/75">{t.emergencyBody}</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10">
              {t.sosCountries.map((c) => (
                <a
                  key={c.country}
                  href={`tel:${c.num}`}
                  data-testid={`help-sos-${c.num}`}
                  className="bg-[#0A0A0A] p-5 hover:bg-[#1A0809] transition-colors"
                >
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest text-white/50">
                    {c.country}
                  </div>
                  <div className="font-display text-4xl text-redflag mt-1">
                    {c.num}
                  </div>
                  <div className="text-xs text-white/55 mt-1">{c.who}</div>
                </a>
              ))}
            </div>
          </div>

          <Block
            title={t.helplineTitle}
            icon={<MessageCircle size={22} className="text-redflag" />}
          >
            {t.helplines.map((r, i) => (
              <ContactRow key={i} row={r} />
            ))}
          </Block>

          <Block
            title={t.domesticTitle}
            icon={<ShieldCheck size={22} className="text-redflag" />}
          >
            {t.domestic.map((r, i) => (
              <ContactRow key={i} row={r} />
            ))}
          </Block>

          <Block
            title={t.psyTitle}
            icon={<Heart size={22} className="text-redflag" />}
          >
            {t.psy.map((r, i) => (
              <ContactRow key={i} row={r} />
            ))}
          </Block>

          {/* Partners */}
          <section className="mt-16">
            <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight">
              {t.partnersTitle}
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {t.partners.map((p, i) => (
                <div
                  key={i}
                  data-testid={`help-partner-${i}`}
                  className="bg-[#0A0A0A] p-6 md:p-8"
                >
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest text-redflag">
                    {p.role}
                  </div>
                  <div className="mt-2 font-display uppercase text-xl text-white tracking-tight">
                    {p.name}
                  </div>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Footnote */}
          <div className="mt-16 border-l-2 border-redflag pl-5 max-w-3xl">
            <div className="overline">{t.note}</div>
            <p className="mt-2 text-sm text-white/65 leading-relaxed">
              {t.noteBody}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
